import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Inject,
    Input,
    LOCALE_ID,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    QueryList,
    ViewChild,
    ViewChildren,
} from '@angular/core';
import { CdkTextareaAutosize } from '@angular/cdk/text-field';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatExpansionPanel } from '@angular/material/expansion';
import { MatTabGroup } from '@angular/material/tabs';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService, CanComponentDeactivate } from '@klickdata/core/auth';
import { ConfigService } from '@klickdata/core/config';
import { Customer } from '@klickdata/core/customer';
import { FormHelper } from '@klickdata/core/form';
import { MediaType } from '@klickdata/core/media/src/media-type';
import { MediaComponent } from '@klickdata/core/media/src/media.component';
import { MediaService } from '@klickdata/core/media/src/media.service';
import { MessageSavedComponent, MessageService } from '@klickdata/core/message';
import { MessageErrorComponent } from '@klickdata/core/message/src/message-error/message-error.component';
import { MobileService, SideNaveActionsTypes, SideNaveDataTypes } from '@klickdata/core/mobile';
import { ResourceMediaTypes } from '@klickdata/core/question/src/question-type/question-type';
import {
    AppScope,
    Resource,
    ResourceComposerDirective,
    ResourceService,
    ResourceSocketService,
    ResourceTypes,
} from '@klickdata/core/resource';
import { ResourceBuilderComposerDirective } from '@klickdata/core/resource/src/resource-builder-composer-directive';
import { User } from '@klickdata/core/user';
import { Utils } from '@klickdata/core/util';
import { FileUploader } from 'ng2-file-upload';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { catchError, filter, map, shareReplay, switchMap, takeUntil } from 'rxjs/operators';
import { ResourceBuilderComponent } from '../resource-builder/resource-builder.component';
import { ResourceManagerControlsComponent } from './resource-manager-controls/resource-manager-controls.component';
import { ResourceManagerEvent } from './resource-manager-event';

@Component({
    selector: 'app-resource-manager',
    templateUrl: './resource-manager.component.html',
    styleUrls: ['./resource-manager.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ResourceManagementComponent
    implements OnInit, OnDestroy, CanComponentDeactivate, OnChanges, AfterViewInit
{
    @ViewChild('compRef') resourceComposer: ResourceComposerDirective;
    @ViewChild('pulishCheckbox') pulishCheckbox: MatCheckbox;
    @ViewChild(MediaComponent) mediaCompRef: MediaComponent;
    @ViewChild('mainTabs') mainTabs: MatTabGroup;
    @ViewChild('coverImagePanel') coverImagePanel: MatExpansionPanel;
    @ViewChild('resToolsComp') resToolsComp: ResourceManagerControlsComponent;
    @ViewChildren('title', { read: ElementRef }) titleTextareas: QueryList<ElementRef<HTMLTextAreaElement>>;
    @ViewChildren('title', { read: CdkTextareaAutosize }) titleAutosizes: QueryList<CdkTextareaAutosize>;
    public destroy: Subject<boolean> = new Subject<boolean>();
    public resIsPublished: Subject<boolean> = new Subject<boolean>();
    public loadingUploader: Subject<boolean> = new Subject<boolean>();
    public loadingImgProcessing: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public loadingTitleProcessing: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public loadingBulletsProcessing: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public isAIUploadedImg: Subject<boolean> = new Subject<boolean>();
    public resKeysValidaty: { [key: string]: string | boolean }[];
    ResourceTypes = ResourceTypes;
    public defaultResourceSummaryCount: number;
    public defaultResourceGoalCount: number;
    @Input() public resourceForm: FormGroup;
    @Input() resource: Resource | null;
    @Input() typeId: ResourceTypes;
    @Input() public fieldsLabels: { [key: string]: string }[];
    public loading: boolean;
    @Output() saved: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Input() user: User;
    public customer$: Observable<Customer>;
    ResourceManagerEvent = ResourceManagerEvent;
    public uploader: FileUploader;
    public isMobile: boolean;
    public mediaAIPrompterText: BehaviorSubject<string> = new BehaviorSubject<string>(null);

    constructor(
        protected resourceService: ResourceService,
        protected fb: FormBuilder,
        protected cdr: ChangeDetectorRef,
        protected router: Router,
        protected mobileService: MobileService,
        protected configService: ConfigService,
        protected auth: AuthService,
        protected mediaService: MediaService,
        protected elementRef: ElementRef,
        protected messageService: MessageService,
        protected route: ActivatedRoute,
        @Inject(LOCALE_ID) protected localeId: string
    ) {
        this.defaultResourceSummaryCount = this.configService.config.defaultResourceSummaryCharCount;
        this.defaultResourceGoalCount = this.configService.config.defaultResourceGoalCharCount;
        this.customer$ = this.auth.getCustomer().pipe(shareReplay());
    }

    ngOnInit(): void {
        this.buildForm();

        if (this.resource) {
            this.updateForm();
        }

        this.resourceForm
            .get('publish')
            .valueChanges.pipe(takeUntil(this.destroy))
            .subscribe((value) => {
                value ? this.resourceForm.get('public').enable() : this.resourceForm.get('public').disable();

                if (!value) {
                    this.resourceForm.get('public').setValue(false);
                }
            });
        this.mobileService.isMobile().subscribe((isMob) => (this.isMobile = isMob));

        // The title field is rendered in both the "Content" and "Extra" tabs using the same
        // form control. Reactive forms skip pushing user-typed values back to other inputs bound
        // to the same control, so we manually mirror the value into every title field while a user
        // edits one of them, keeping all usages in sync.
        this.resourceForm
            .get('title')
            .valueChanges.pipe(takeUntil(this.destroy))
            .subscribe((value: string) => this.syncTitleFields(value));
    }

    private syncTitleFields(value: string): void {
        const textareas = this.titleTextareas?.toArray() ?? [];
        const autosizes = this.titleAutosizes?.toArray() ?? [];
        const nextValue = value ?? '';

        textareas.forEach((textareaRef, index) => {
            const textarea = textareaRef.nativeElement;
            // Skip the field currently being edited to avoid moving the caret, only update the rest.
            if (textarea !== document.activeElement && textarea.value !== nextValue) {
                textarea.value = nextValue;
                autosizes[index]?.resizeToFitContent(true);
            }
        });

        this.cdr.markForCheck();
    }

    ngAfterViewInit(): void {
        const contextParam = this.route.snapshot.queryParamMap.get('context');
        if (contextParam === 'occasions' && this.resourceComposer) {
            this.mainTabs.selectedIndex = 1;
        }
        this.resourceComposer?.onResourcePromptStateChange
            .asObservable()
            .pipe(takeUntil(this.destroy))
            .subscribe((resource: Resource) => {
                this.loadingImgProcessing.next(resource.isProcessingState('image'));
                this.loadingTitleProcessing.next(resource.isProcessingState('title'));
                this.loadingBulletsProcessing.next(resource.isProcessingState('title'));
                // Keep tags editable while title/tags prompts run (e.g. SCORM parse) so the user
                // can keep adding tags; auto-generated tags are merged in updateResource/updateForm.
                this.resourceService.setIsTrailerProcessing(
                    this.resource.id,
                    resource.isProcessingState('video/trailer')
                );
                this.resourceService.setIsPresentationProcessing(
                    this.resource.id,
                    resource.isProcessingState('video/presentation')
                );
            });
    }

    ngOnChanges(): void {
        if (this.resource) {
            this.updateForm();
        }
    }

    public setLoading(isLoading: boolean): void {
        this.loading = isLoading;
        this.cdr.markForCheck();
    }

    onTabSelectionChange(activeIndex: number) {
        this.resourceComposer.onTabSelectionChange(activeIndex);
    }
    private updateForm() {
        this.customer$.pipe(takeUntil(this.destroy)).subscribe((customer) => {
            if (
                !this.resource.collaboration?.view?.metadata?.name_translation ||
                Utils.isEmpty(this.resource.collaboration?.view?.metadata?.name_translation)
            )
                this.resource.labelTranslation = {
                    [this.localeId === 'en' ? 'en-US' : this.localeId]:
                        customer.scope_names[ResourceTypes.scopeByChildType(this.typeId)] ??
                        AppScope.label(ResourceTypes.scopeByChildType(this.typeId)),
                };
        });
        const tagCtrl = this.resourceForm.get('tag_ids');
        const localTagIds = (tagCtrl?.value as number[]) ?? [];
        const tagsWereDirty = !!tagCtrl?.dirty;
        const remoteTagIds = this.resource.tag_ids ?? [];

        // Never let a resource refresh wipe tags the user is still editing. Merge auto-generated
        // tags into the local selection when the control is dirty; otherwise take the server list.
        const { tag_ids: _remoteTagIds, ...resourceWithoutTags } = this.resource as any;
        this.resourceForm.patchValue(resourceWithoutTags);
        tagCtrl?.setValue(tagsWereDirty ? [...new Set([...localTagIds, ...remoteTagIds])] : remoteTagIds, {
            emitEvent: false,
        });

        FormHelper.resetForm(this.resourceForm);
        this.resourceForm.get('publish').patchValue(!!this.resource.last_publish);
        if (tagsWereDirty) {
            tagCtrl?.markAsDirty();
            this.resourceForm.markAsDirty({ onlySelf: true });
        }
        this.cdr.markForCheck();
    }

    private buildForm() {
        this.resourceForm = this.fb.group({
            id: [],
            publish: [false],
            title: ['', Validators.required],
            public: [{ value: false, disabled: true }],
            language_id: ['', Validators.required],
            tag_ids: [[]],
            media_id: [],
            category_ids: [[], Validators.required],
            priority: [],
            description: [''],
            manager_ids: [[]],
            instructions: [''],
            other_info: [''],
            bullets: [''],
            price: [''],
            currency: ['USD'],
            users_attach: [],
            users_detach: [],
            groups_attach: [],
            groups_detach: [],
            sync_all_users: [],
            sync_all_groups: [],
            users_access_open: [],
            staff: this.fb.group({
                educator: [[]],
                manager: [[this.user.id]],
                publisher: [[]],
                host: [[]],
                editor: [[]],
                organizer: [[]],
            }),
            metadata: this.fb.group({
                other_info: [false],
                medical_exam: [false],
            }),
            medias: this.fb.group({
                background: [],
                screenshot: [],
                publisher: [],
                trailer: [],
                presentation: [],
                brochure: [[]],
                exercise: [[]],
            }),
            labelTranslation: [],
            duplicable: [true],
            custom_fields: this.fb.array([]),
        });
    }

    clearField(field: string) {
        this.resourceForm.get(field).setValue(null);
        this.resourceForm.get(field).markAsDirty();
    }

    public getFieldLabel(key: string, isTitleLabel?: boolean): string {
        if (this.fieldsLabels) {
            const item = this.fieldsLabels.find((item) => item.key === key);
            return isTitleLabel ? item?.title : item?.info;
        }
    }
    public showMobileToolTip(key: string) {
        if (this.fieldsLabels && this.isMobile) {
            const item = this.fieldsLabels.find((item) => item.key === key);
            this.messageService.openMessage(MessageSavedComponent, item.info);
        }
    }
    public addMedia(errorMsg?: string) {
        if (this.resourceForm.value.media_id) {
            this.mainTabs.selectedIndex = 1;
            this.coverImagePanel.open();
            return;
        }
        this.mobileService.updateSideNavSub({
            dataType: SideNaveDataTypes.ADD_RESOURCE_MEDIA,
            data: {
                icon: 'add_photo_alternate',
                title: $localize`Add an image`,
                description: $localize`Please select your preferred way to add an image.`,
                type: this.typeId,
                errorMsg: errorMsg,
            },
        });
        this.getMediaTypeFromSlideIn();
    }
    private getMediaTypeFromSlideIn() {
        this.mobileService
            .getSideNavAction()
            .pipe(
                filter((action) => action === SideNaveActionsTypes.POSITIVE),
                switchMap(() => this.mobileService.getSideNavResponseData()),
                filter((data) => data.type === this.typeId),
                takeUntil(this.destroy)
            )
            .subscribe((data) => {
                if (data && data.value) {
                    if (data.value === ResourceMediaTypes.FILE) {
                        this.isAIUploadedImg.next(false);
                        this.mediaAIPrompterText.next(null);
                        this.resourceForm.patchValue({ media_id: data.mediaId });
                        this.resourceForm.markAsDirty();
                        this.cdr.detectChanges();
                        this.cdr.markForCheck();
                    } else {
                        this.loadingUploader.next(true);
                        this.initAIUpload(data);
                    }
                }
            });
    }
    private initAIUpload(data: any) {
        this.mediaService
            .uploadMediaByLink({
                prompt: data.aiText,
                type: MediaType.PROMPT,
                scope_id: ResourceTypes.scopeByChildType(this.typeId),
            })
            .pipe(
                takeUntil(this.destroy),
                catchError((err) => {
                    this.handleAIRequestFailure(err);
                    return of(null);
                })
            )
            .subscribe((media) => {
                this.loadingUploader.next(false);
                if (media && media.id) {
                    this.isAIUploadedImg.next(true);
                    this.resourceForm.patchValue({ media_id: media.id });
                    this.mediaAIPrompterText.next(data.aiText);
                    this.resourceForm.markAsDirty();
                }
            });
    }
    handleAIRequestFailure(err) {
        if (err && err.error && err.error.error) {
            this.messageService.openMessage(MessageErrorComponent, err.error.error.messages.join('/n'));
            this.addMedia(err.error.error.messages.join('/n'));
        }
    }

    public review() {
        if (this.resource && this.resource.id) {
            this.router.navigate(['/home/dashboard/resource-details/' + this.resource.id]);
        }
    }
    public list() {
        this.router.navigate(['../'], { relativeTo: this.route });
    }

    public canDeactivate(): boolean {
        return this.resourceComposer.canDeactivate();
    }

    public saveAndDeactivate(): Observable<boolean> {
        return this.resourceComposer.saveAndDeactivate();
    }

    public openCollaborationSheet() {
        this.resourceComposer.openCollaborationSheet();
    }

    @HostListener('window:beforeunload', ['$event'])
    handleClose($event) {
        setTimeout(() => {
            $event.returnValue = this.canDeactivate();
        });
    }

    public ngOnDestroy(): void {
        this.destroy.next(true);
        this.destroy.unsubscribe();
    }

    get resourceBuilder(): ResourceBuilderComponent | null {
        return ResourceTypes.isTypeThatHasItems(this.typeId) && this.isResourceBuilder
            ? (this.resourceComposer as ResourceBuilderComposerDirective).resourceBuilder
            : null;
    }

    get isResourceBuilder(): boolean {
        return this.resourceComposer instanceof ResourceBuilderComposerDirective;
    }
}
